import React, { useCallback } from 'react';

import commonStyles from 'pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/components/ui/table';
import styles from '../composizione-del-portafoglio/components/ComposizioneDelPortafoglio.module.scss';
import GlobalTooltip from '../composizione-del-portafoglio/components/GlobalTooltip';
import { hideGlobalTooltip, showGlobalTooltip, updateGlobalTooltipText } from '../composizione-del-portafoglio/components/utils';
import { createLogger } from 'shared/logger';

const log = createLogger('Negoziabilita');

// Full-width table with proportional columns: PortfolioTable's fixed pixel widths
// would leave dead space in wide cards for a 6-column table.
const COLUMNS: { key: string; label: string; width: string }[] = [
  { key: 'isin', label: 'ISIN', width: '14%' },
  { key: 'tipo_prodotto', label: 'Tipo prodotto', width: '11%' },
  { key: 'nome', label: 'Nome', width: '33%' },
  { key: 'valuta', label: 'Valuta', width: '8%' },
  { key: 'negoziabile', label: 'Negoziabile', width: '15%' },
  { key: 'consulenziabile_banca', label: 'Consulenziabile banca', width: '19%' },
];

type SectionChildrenProps = {
  reportData: any;
};

const NegoziabilitaFinecoContainer: React.FC<SectionChildrenProps> = ({ reportData }) => {
  const handleTooltipTrigger = useCallback((event: React.MouseEvent, text: string) => {
    showGlobalTooltip(event, text);
  }, []);

  const handleCellClick = useCallback((text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        updateGlobalTooltipText('Copied');
      })
      .catch((err) => {
        log.warn('copy to clipboard failed', { error: err });
      });
  }, []);

  if (!reportData?.negoziabilita?.length) {
    return null;
  }

  return (
    <div className={commonStyles.sectionContainer}>
      <div className={styles.complexTableContainer}>
        <div className={styles.tableContainerInner}>
          <style>
            {`
            .negoziabilitaTable th {
              border-bottom: none !important;
              padding: 8px 16px;
              position: relative;
            }

            .negoziabilitaTable tbody tr:first-child td {
              border-top: none !important;
              padding-top: 8px !important;
            }
          `}
          </style>
          <Table className={`${styles.table} negoziabilitaTable`}>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableHead key={col.key} style={{ fontWeight: 'bold', width: col.width, textAlign: 'center' }}>
                    {col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportData.negoziabilita.map((row: Record<string, string>, index: number) => (
                <TableRow key={index}>
                  {COLUMNS.map((col) => {
                    const value = row[col.key] ?? '';
                    return (
                      <TableCell
                        key={col.key}
                        style={{ textAlign: 'center' }}
                        onMouseEnter={(e) => handleTooltipTrigger(e, value)}
                        onMouseLeave={hideGlobalTooltip}
                        onClick={() => handleCellClick(value)}
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <GlobalTooltip />
        </div>
      </div>
    </div>
  );
};

export default React.memo(
  NegoziabilitaFinecoContainer,
  (prevProps: SectionChildrenProps, nextProps: SectionChildrenProps) => prevProps.reportData === nextProps.reportData,
);
